import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import heroImage from "../assets/zeamays.png";
import ProductImage from "../components/ProductImage";
import Ogbono from "../assets/17.jpeg";
import CrayfishPowder from "../assets/19.jpeg";
import YamFlour from "../assets/12.jpeg";
import LocustBeans from "../assets/14.jpeg";
import SpicedMilletPap from "../assets/18.jpeg";
import SativaPoundo from "../assets/15.jpeg";
import SpicedYellowPap from "../assets/16.jpeg";
import BeansPowder from "../assets/13.jpeg";
import { getActiveProducts } from "../services/storefrontProducts";
import { loadActivePromotions } from "../services/promotions";
import { PROBIODELIGHTS_CUSTOMER_LAUNCH_ENABLED } from "../config/featureFlags";




const featuredProducts = [
  { title: "Ogbono", imageSrc: Ogbono, description: "A rich, earthy seasoning for soups and stews." },
  { title: "Crayfish Powder", imageSrc: CrayfishPowder, description: "Freshly prepared for deeply savory meals." },
  { title: "Yam Flour", imageSrc: YamFlour, description: "A staple for smooth, satisfying dishes." },
  { title: "InstaMoi", imageSrc: BeansPowder, description: "Bold spice for everyday cooking." },
  { title: "Locust Beans", imageSrc: LocustBeans, description: "A classic taste enhancer with unmistakable depth." },
  { title: "Spiced Millet Pap", imageSrc: SpicedMilletPap, description: "Comforting and wholesome for all occasions." },
  { title: "Sativa Poundo", imageSrc: SativaPoundo, description: "Soft, easy-to-prepare and family-friendly." },
  { title: "Spiced Yellow Pap", imageSrc: SpicedYellowPap, description: "Flavorful and convenient for quick meals." },
];

// Keep these marketing slides local and explicit. Add this asset when available:
// src/assets/probiodelights-hero.jpg
const heroSlides = [
  {
    eyebrow: "Zea Mays Foods / Pantry",
    title: "Quality Nigerian food products, made easy to prepare.",
    description: "Stock your kitchen with trusted pantry staples, prepared with care for flavorful everyday meals.",
    image: Ogbono,
    imageAlt: "Zea Mays pantry product",
    cta: "Shop Pantry",
    link: "/products?business_line=Pantry",
  },
  {
    eyebrow: "ProBioDelights",
    title: "Discover fresh, wholesome possibilities.",
    description: "Explore fermented drinks and nourishing products from another part of the Zea Mays Foods family.",
    image: null,
    imagePath: "src/assets/probiodelights-hero.jpg",
    imageAlt: "ProBioDelights product imagery needed",
    cta: "Explore ProBioDelights",
    link: "/products?business_line=ProBioDelights",
  },
  {
    eyebrow: "Zea Mays Foods",
    title: "One store for thoughtful food choices.",
    description: "Browse pantry essentials and ProBioDelights products together, then order securely from our live catalog.",
    image: heroImage,
    imageAlt: "Zea Mays Foods brand",
    cta: "Shop All",
    link: "/products",
  },
];

const legacyHeroSlide = {
  eyebrow: "Fresh pantry staples from Zea Mays Foods",
  title: "Quality Nigerian food products, made easy to prepare.",
  description: "Shop quality Nigerian food products from Zea Mays Foods. Browse our products, place your order online, and we'll contact you to confirm local delivery arrangements.",
  image: heroImage,
  imageAlt: "Zea Mays Foods hero banner",
  cta: "Shop Our Products",
  link: "/products",
};

const customerHeroSlides = PROBIODELIGHTS_CUSTOMER_LAUNCH_ENABLED ? heroSlides : [legacyHeroSlide];

const HomePage = () => {
  const [featuredCatalog, setFeaturedCatalog] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [activeSlide, setActiveSlide] = useState(0);
  const [heroPaused, setHeroPaused] = useState(false);

  useEffect(() => {
    if (heroPaused) return undefined;
    const timer = window.setInterval(() => {
      setActiveSlide((current) => (current + 1) % customerHeroSlides.length);
    }, 1000);
    return () => window.clearInterval(timer);
  }, [heroPaused]);

  useEffect(() => {
    getActiveProducts()
      .then((products) => setFeaturedCatalog(products))
      .catch(() => setFeaturedCatalog([]));
  }, []);

  useEffect(() => {
    loadActivePromotions().then(setPromotions).catch(() => setPromotions([]));
  }, []);

  const slide = customerHeroSlides[activeSlide];
  const moveSlide = (direction) => {
    setActiveSlide((current) => (current + direction + customerHeroSlides.length) % customerHeroSlides.length);
  };

  return (
    <div className="w-full">
      <section
        className="relative mt-16 overflow-hidden bg-[radial-gradient(circle_at_top_left,_rgba(190,242,100,0.22),_transparent_30%),radial-gradient(circle_at_top_right,_rgba(185,28,28,0.16),_transparent_25%),linear-gradient(180deg,#0f2417_0%,#17311f_55%,#f7fbef_100%)]"
        aria-label="Zea Mays Foods highlights"
        onMouseEnter={() => setHeroPaused(true)}
        onMouseLeave={() => setHeroPaused(false)}
        onFocus={() => setHeroPaused(true)}
        onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setHeroPaused(false); }}
      >
        <div className="absolute inset-0 bg-[linear-gradient(120deg,rgba(15,23,42,0.42),rgba(15,23,42,0.15))]" />
        <div className="relative mx-auto grid min-h-[720px] max-w-7xl items-center gap-10 px-6 py-16 lg:grid-cols-2 lg:px-8">
          <div key={activeSlide} className="max-w-2xl text-white motion-safe:animate-[fadeIn_500ms_ease-out]" aria-live="polite">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.24em] text-lime-100 backdrop-blur">
              {slide.eyebrow}
            </div>
            <h1 className="mt-6 text-5xl font-black tracking-tight text-white sm:text-6xl lg:text-7xl">
              {slide.title}
            </h1>
            <p className="mt-6 max-w-xl text-base leading-8 text-slate-100 sm:text-lg">
              {slide.description}
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link
                to={slide.link}
                className="inline-flex items-center justify-center rounded-full bg-red-700 px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-red-950/20 transition hover:bg-red-800"
              >
                {slide.cta}
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center rounded-full border border-white/20 bg-white/10 px-6 py-3.5 text-sm font-semibold text-white backdrop-blur transition hover:bg-white/15"
              >
                Talk to us
              </Link>
            </div>
            <div className="mt-8 grid max-w-xl grid-cols-2 gap-3 sm:grid-cols-4">
              {[
                { label: "Live catalog", value: "Updated daily" },
                { label: "Fast checkout", value: "Secure payment" },
                { label: "Order tracking", value: "Real-time" },
                { label: "Fresh stock", value: "Managed" },
              ].map((item) => (
                <div key={item.label} className="rounded-2xl border border-white/15 bg-white/10 px-4 py-3 backdrop-blur">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-lime-100/80">{item.label}</p>
                  <p className="mt-1 text-sm font-bold text-white">{item.value}</p>
                </div>
              ))}
            </div>
          </div>

          <div key={`image-${activeSlide}`} className="relative motion-safe:animate-[fadeIn_500ms_ease-out]">
            <div className="absolute -left-6 top-10 h-28 w-28 rounded-full bg-lime-300/20 blur-3xl" />
            <div className="absolute -right-4 bottom-8 h-36 w-36 rounded-full bg-red-500/20 blur-3xl" />
            <div className="relative overflow-hidden rounded-[2rem] border border-white/20 bg-white/10 p-3 shadow-[0_30px_80px_rgba(15,23,42,0.35)] backdrop-blur-xl">
              {slide.image ? (
                <img src={slide.image} alt={slide.imageAlt} className="h-[380px] w-full rounded-[1.5rem] bg-slate-950/10 object-cover sm:h-[520px]" width="1200" height="1600" loading="eager" fetchPriority="high" decoding="async" />
              ) : (
                <div className="flex h-[380px] w-full items-center justify-center rounded-[1.5rem] bg-gradient-to-br from-lime-100 via-emerald-100 to-slate-100 p-8 text-center sm:h-[520px]">
                  <div><p className="text-sm font-bold uppercase tracking-[0.24em] text-emerald-900">ProBioDelights</p><p className="mt-3 text-2xl font-black text-slate-900">Fresh imagery coming soon</p><p className="mt-2 text-sm text-slate-600">Add {slide.imagePath} to complete this slide.</p></div>
                </div>
              )}
              <div className="absolute inset-x-6 bottom-6 rounded-[1.25rem] border border-white/15 bg-slate-950/55 p-4 text-white backdrop-blur">
                <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-lime-200">
                  {PROBIODELIGHTS_CUSTOMER_LAUNCH_ENABLED ? (heroPaused ? "Paused" : "Auto-rotating highlights") : "Customer favorite"}
                </p>
                <p className="mt-1 text-lg font-bold">{PROBIODELIGHTS_CUSTOMER_LAUNCH_ENABLED ? slide.eyebrow : "A cleaner way to shop pantry essentials."}</p>
                <p className="mt-1 text-sm text-slate-200">
                  {PROBIODELIGHTS_CUSTOMER_LAUNCH_ENABLED ? "Discover a clearer way to shop the Zea Mays Foods family." : "Easy browsing, strong visuals, and reliable product data from our store."}
                </p>
              </div>
            </div>
            {customerHeroSlides.length > 1 ? <div className="absolute inset-x-8 bottom-[-3.5rem] flex items-center justify-between sm:inset-x-12">
              <button type="button" onClick={() => moveSlide(-1)} className="rounded-full border border-white/30 bg-slate-950/50 p-2 text-white" aria-label="Previous hero slide"><ChevronLeft size={20} /></button>
              <div className="flex gap-2" role="tablist" aria-label="Hero slides">
                {customerHeroSlides.map((item, index) => <button key={item.eyebrow} type="button" role="tab" aria-selected={index === activeSlide} aria-label={`Show ${item.eyebrow} slide`} onClick={() => setActiveSlide(index)} className={`h-2.5 rounded-full transition ${index === activeSlide ? "w-8 bg-lime-300" : "w-2.5 bg-white/60"}`} />)}
              </div>
              <button type="button" onClick={() => moveSlide(1)} className="rounded-full border border-white/30 bg-slate-950/50 p-2 text-white" aria-label="Next hero slide"><ChevronRight size={20} /></button>
            </div> : null}
          </div>
        </div>
      </section>

      {promotions.length ? (
        <section className="bg-amber-50 px-6 py-16">
          <div className="mx-auto max-w-7xl">
            <p className="text-sm font-semibold uppercase tracking-[0.28em] text-red-700">Limited-time offers</p>
            <div className="mt-6 grid gap-6 md:grid-cols-2">
              {promotions.map((promotion) => (
                <article key={promotion.id} className="overflow-hidden rounded-[2rem] border border-amber-200 bg-white shadow-sm">
                  {promotion.image_url ? <img src={promotion.image_url} alt={promotion.name} className="h-56 w-full object-cover" /> : null}
                  <div className="p-6">
                    <h2 className="text-2xl font-bold text-slate-950">{promotion.name}</h2>
                    <p className="mt-2 text-slate-600">{promotion.description}</p>
                    <p className="mt-4 font-bold text-red-700">{promotion.discount_type === "percentage" ? `${promotion.discount_value}% off` : `NGN ${promotion.discount_value} off`}</p>
                    <Link to="/products" className="mt-5 inline-flex rounded-full bg-slate-950 px-5 py-3 text-sm font-semibold text-white">Shop the offer</Link>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {PROBIODELIGHTS_CUSTOMER_LAUNCH_ENABLED ? <section className="border-y border-emerald-100 bg-emerald-950 px-6 py-14 text-white">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-6 sm:flex-row sm:items-center">
          <div>
            <p className="text-sm font-bold uppercase tracking-[0.28em] text-lime-300">More from Zea Mays Foods</p>
            <h2 className="mt-2 text-3xl font-black">Meet ProBioDelights</h2>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-emerald-100">Discover another product line of fresh, wholesome and fermented food products in the same live catalog.</p>
          </div>
          <Link to="/products?business_line=ProBioDelights" className="shrink-0 rounded-full bg-lime-300 px-5 py-3 text-sm font-bold text-emerald-950 transition hover:bg-lime-200">Explore ProBioDelights</Link>
        </div>
      </section> : null}

      <ProductShowcase
        label="Pantry"
        title="Explore our pantry essentials"
        description="A visual look at the Zea Mays Foods products available in our store."
        products={featuredProducts}
        cta={{ to: "/products?business_line=Pantry", label: "Shop Pantry" }}
      />

      {PROBIODELIGHTS_CUSTOMER_LAUNCH_ENABLED ? (
        <ProductShowcase
          label="ProBioDelights"
          title="Discover wholesome choices"
          description="Explore the ProBioDelights products available across our growing food family."
          products={featuredCatalog.filter((product) => product.business_line === "ProBioDelights")}
          cta={{ to: "/products?business_line=ProBioDelights", label: "Shop ProBioDelights" }}
          emptyMessage="ProBioDelights products will appear here as they become available."
        />
      ) : null}
    </div>
  );
};

const ProductShowcase = ({ label, title, description, products, cta, emptyMessage }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const items = Array.isArray(products) ? products.filter((product) => product?.image || product?.imageSrc) : [];

  useEffect(() => {
    if (paused || items.length <= 1) return undefined;
    const timer = window.setInterval(() => {
      setActiveIndex((current) => (current + 1) % items.length);
    }, 3000);
    return () => window.clearInterval(timer);
  }, [items.length, paused]);

  const activeProduct = items.length ? items[activeIndex % items.length] : null;
  const slideAnimation = [
    "motion-safe:animate-[slideInFromLeft_700ms_ease-out]",
    "motion-safe:animate-[slideInFromBottom_700ms_ease-out]",
    "motion-safe:animate-[slideInFromRight_700ms_ease-out]",
    "motion-safe:animate-[slideInFromTop_700ms_ease-out]",
  ][activeIndex % 4];

  return (
    <section
      className="relative min-h-[430px] overflow-hidden bg-slate-950 text-left sm:min-h-[560px]"
      aria-label={`${label} product showcase`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {activeProduct ? (
        <div key={`${activeProduct.id || activeProduct.title || activeProduct.name}-${activeIndex}`} className={`absolute inset-0 ${slideAnimation}`}>
          <ProductImage
            src={activeProduct.image || activeProduct.imageSrc}
            alt={activeProduct.name || activeProduct.title || `${label} product`}
            className="h-full w-full object-cover"
            width="1600"
            height="900"
            loading="lazy"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/45 to-black/20" />
          <div className="absolute inset-0 flex items-center px-6 py-16 sm:px-12 lg:px-20">
            <div className="max-w-xl text-white">
              <p className="text-sm font-bold uppercase tracking-[0.3em] text-lime-200">{label}</p>
              <h2 className="mt-4 text-4xl font-black tracking-tight sm:text-6xl">{title}</h2>
              <p className="mt-5 max-w-lg text-base leading-7 text-slate-100 sm:text-lg">{description}</p>
              <Link to={cta.to} className="mt-8 inline-flex rounded-full bg-white px-6 py-3.5 text-sm font-bold text-slate-950 transition hover:bg-lime-200">{cta.label}</Link>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex min-h-[430px] items-center justify-center bg-slate-900 px-6 text-center text-white sm:min-h-[560px]">
          <div><p className="text-sm font-bold uppercase tracking-[0.3em] text-lime-200">{label}</p><p className="mt-4 text-slate-200">{emptyMessage || "Products will appear here soon."}</p></div>
        </div>
      )}
    </section>
  );
};

export default HomePage;
